import { eventNames, useCoreEvents } from '@carvana/core-events';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { OnEscapeListener } from '@carvana/merch-components';
import { MfeLoad } from '@carvana/microfrontend';
import { MfeReactComponent } from '@carvana/microfrontend-react';
import { useContext, useEffect, useMemo } from 'react';
import { getMfeCacheBuster } from '@/common/MicroFrontEnds';
import { useClientSide } from '~/hooks/common';

const SignInPlaceholder = () => <span />;

export default function AccountModal() {
  const { trackEvent } = useContext(AnalyticsContext);
  const isClientSide = useClientSide();
  const { emit } = useCoreEvents();

  const authenticationConfig = useMemo(
    () => ({
      CallerIdentifier: 'SEMLandingPage',
      EnterEmail: {
        onSuccess: (isValid: boolean, hasAccount: boolean) => {
          trackEvent('AccountModal-EnteredEmail', { isValid, hasAccount });
        },
        onFailure: (error: any) => {
          trackEvent('AccountModal-EnteredEmail-Error', { error });
        }
      },
      OnAuthenticatedSuccess: () => {
        trackEvent('AccountModal-Submitted');
      },
      SignIn: {
        onSuccess: () => {
          trackEvent('AccountModal-SignIn-Successful');
        },
        onFailure: (error: any) => {
          trackEvent('AccountModal-SignIn-Error', { error });
        }
      },
      CreateAccount: {
        onSuccess: () => {
          trackEvent('AccountModal-CreateAccount-Successful');
        },
        onFailure: (error: any) => {
          trackEvent('AccountModal-CreateAccount-Error', { error });
        }
      },
      SocialLogins: {
        onSuccess: () => {
          trackEvent('AccountModal-SocialLogins-Successful');
        },
        onFailure: (error: any) => {
          trackEvent('AccountModal-SocialLogins-Error', { error });
        }
      },
      ForgotPassword: {
        onSuccess: () => {
          trackEvent('AccountModal-ForgotPassword-Successful');
        },
        onFailure: (error: any) => {
          trackEvent('AccountModal-ForgotPassword-Error', { error });
        }
      },
      ResetPassword: {
        onSuccess: () => {
          trackEvent('AccountModal-ResetPassword-Successful');
        },
        onFailure: (error: any) => {
          trackEvent('AccountModal-ResetPassword-Error', { error });
        }
      },
      GoogleOneTap: {
        enabled: true,
        onSuccess: () => {
          trackEvent('AccountModal-GoogleOneTap-Successful');
        },
        onFailure: (error: any) => {
          trackEvent('AccountModal-GoogleOneTap-Error', { error });
        }
      }
    }),
    [trackEvent]
  );

  useEffect(() => {
    void MfeLoad({
      files: [
        [
          'AccountModal',
          [
            {
              component: 'default',
              chunks: ['index.css'],
              priority: 'high',
              default: {
                file: 'index.js',
                manifest: `${
                  process.env.CVNA_APP_CDN_URL
                }/authentication-flows/account-modal/assets-manifest.json?v=${getMfeCacheBuster()}`
              }
            }
          ]
        ]
      ]
    });
  }, []);

  return isClientSide ? (
    <OnEscapeListener
      onEscape={() => {
        emit(eventNames.accountModal.close);
      }}
    >
      <MfeReactComponent
        module="AccountModal"
        component="default"
        config={authenticationConfig}
        Anchor={SignInPlaceholder}
      />
    </OnEscapeListener>
  ) : null;
}
